import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import moment from 'moment';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Building2, Check, X, Percent, Search, Clock, CreditCard,
  Mail, Phone, Car, Edit2, ChevronDown, ChevronUp, AlertCircle,
  BarChart2, ListChecks, Send, Copy, Link2, MessageSquare
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/constants';

const PAYMENT_METHODS = [
  { value: 'invoice', label: 'Reikningur' },
  { value: 'card', label: 'Greiðslukort' },
  { value: 'bank_transfer', label: 'Bankayfirfærsla' },
];

const PAYMENT_TERMS = [15, 30, 45, 60, 90];

function CorporateManagement() {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [search, setSearch] = useState('');

  // Approve dialog
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [approveForm, setApproveForm] = useState({
    discount: 10,
    payment_terms: 30,
    credit_limit: 0,
    payment_method: 'invoice',
    internal_notes: ''
  });
  const [approving, setApproving] = useState(false);

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectApp, setRejectApp] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // Edit dialog (approved)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editApp, setEditApp] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Jobs/history dialog
  const [jobsDialogOpen, setJobsDialogOpen] = useState(false);
  const [jobsApp, setJobsApp] = useState(null);
  const [jobsData, setJobsData] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);

  // Send portal link dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkApp, setLinkApp] = useState(null);
  const [linkForm, setLinkForm] = useState({ email: '', phone: '', send_email: true, send_sms: false });
  const [linkSending, setLinkSending] = useState(false);
  const [linkPortalUrl, setLinkPortalUrl] = useState('');

  // Expand details
  const [expanded, setExpanded] = useState({});

  useEffect(() => { loadApplications(); }, []);

  const loadApplications = async () => {
    try {
      const res = await axios.get(`${API}/corporate-applications`);
      setApplications(res.data || []);
    } catch {
      toast.error('Tókst ekki að hlaða umsóknum');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedApp) return;
    try {
      setApproving(true);
      await axios.put(`${API}/corporate-applications/${selectedApp.id}/approve`, approveForm);
      toast.success(`${selectedApp.company_name} samþykkt – ${approveForm.discount}% afsláttur, ${approveForm.payment_terms} daga greiðslufrestur`);
      setApproveDialogOpen(false);
      setSelectedApp(null);
      await loadApplications();
    } catch {
      toast.error('Tókst ekki að samþykkja umsókn');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectApp) return;
    try {
      setRejecting(true);
      await axios.put(`${API}/corporate-applications/${rejectApp.id}/reject`, { reason: rejectReason });
      toast.success(`Umsókn frá ${rejectApp.company_name} hafnað`);
      setRejectDialogOpen(false);
      setRejectApp(null);
      setRejectReason('');
      await loadApplications();
    } catch {
      toast.error('Tókst ekki að hafna umsókn');
    } finally {
      setRejecting(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!editApp) return;
    try {
      setSaving(true);
      await axios.put(`${API}/corporate-applications/${editApp.id}/update-settings`, editForm);
      toast.success('Stillingar uppfærðar');
      setEditDialogOpen(false);
      setEditApp(null);
      await loadApplications();
    } catch {
      toast.error('Tókst ekki að uppfæra stillingar');
    } finally {
      setSaving(false);
    }
  };

  const openApprove = (app) => {
    setSelectedApp(app);
    setApproveForm({ discount: 10, payment_terms: 30, credit_limit: 0, payment_method: 'invoice', internal_notes: '' });
    setApproveDialogOpen(true);
  };

  const openReject = (app) => {
    setRejectApp(app);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const openEdit = (app) => {
    setEditApp(app);
    setEditForm({
      discount: app.discount || 10,
      payment_terms: app.payment_terms || 30,
      credit_limit: app.credit_limit || 0,
      payment_method: app.payment_method || 'invoice',
      internal_notes: app.internal_notes || ''
    });
    setEditDialogOpen(true);
  };

  const openJobs = async (app) => {
    setJobsApp(app);
    setJobsData(null);
    setJobsDialogOpen(true);
    setJobsLoading(true);
    try {
      const res = await axios.get(`${API}/corporate-applications/${app.id}/jobs`);
      setJobsData(res.data);
    } catch {
      toast.error('Tókst ekki að sækja verkasögu');
    } finally {
      setJobsLoading(false);
    }
  };

  const openSendLink = (app) => {
    setLinkApp(app);
    setLinkForm({
      email: app.contact_email || app.email || '',
      phone: app.contact_phone || app.phone || '',
      send_email: true,
      send_sms: false
    });
    setLinkPortalUrl('');
    setLinkDialogOpen(true);
  };

  const handleSendPortalLink = async () => {
    if (!linkApp) return;
    if (!linkForm.send_email && !linkForm.send_sms) {
      toast.error('Veldu að minnsta kosti eina sendingarleið');
      return;
    }
    if (linkForm.send_email && !linkForm.email) {
      toast.error('Netfang vantar');
      return;
    }
    try {
      setLinkSending(true);
      const res = await axios.post(`${API}/corporate-applications/${linkApp.id}/send-portal-link`, {
        send_email: linkForm.send_email,
        send_sms: linkForm.send_sms,
        email: linkForm.email,
        phone: linkForm.phone
      });
      setLinkPortalUrl(res.data.portal_url || '');
      const r = res.data.results || {};
      const parts = [];
      if (r.email?.sent) parts.push(`tölvupóstur til ${r.email.to}`);
      if (r.sms?.sent) parts.push(`SMS til ${r.sms.to}`);
      toast.success(`Hlekk sendur: ${parts.join(', ') || 'sent'}`);
    } catch {
      toast.error('Tókst ekki að senda hlekk');
    } finally {
      setLinkSending(false);
    }
  };

  const copyPortalUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Hlekkur afritaður');
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const filterApps = (list) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(a =>
      (a.company_name || '').toLowerCase().includes(q) ||
      (a.contact_name || '').toLowerCase().includes(q) ||
      (a.kennitala || '').includes(q) ||
      (a.contact_email || '').toLowerCase().includes(q)
    );
  };

  const pending = filterApps(applications.filter(a => a.status === 'pending'));
  const approved = filterApps(applications.filter(a => a.status === 'approved'));
  const rejected = filterApps(applications.filter(a => a.status === 'rejected'));

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-500 text-sm">Hleð umsóknum...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="h-7 w-7 text-blue-600" />
            Fyrirtækja Umsóknir
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Stjórna fyrirtækjaþjónustu og greiðslustillingum</p>
        </div>

        {/* Stats */}
        <div className="flex gap-3">
          {[
            { label: 'Í bið', count: applications.filter(a => a.status === 'pending').length, color: 'amber' },
            { label: 'Samþykkt', count: applications.filter(a => a.status === 'approved').length, color: 'green' },
            { label: 'Hafnað', count: applications.filter(a => a.status === 'rejected').length, color: 'red' },
          ].map(s => (
            <div key={s.label} className={`bg-${s.color}-50 border border-${s.color}-200 rounded-lg px-4 py-2 text-center min-w-[70px]`}>
              <div className={`text-2xl font-bold text-${s.color}-700`}>{s.count}</div>
              <div className={`text-xs text-${s.color}-600`}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Leita að fyrirtæki, kennitölu..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
          data-testid="corporate-search"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" data-testid="tab-pending">
            Í bið {applications.filter(a => a.status === 'pending').length > 0 && <span className="ml-1.5 bg-amber-500 text-white text-xs rounded-full px-1.5">{applications.filter(a => a.status === 'pending').length}</span>}
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Samþykkt ({applications.filter(a => a.status === 'approved').length})</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Hafnað ({applications.filter(a => a.status === 'rejected').length})</TabsTrigger>
        </TabsList>

        {/* PENDING */}
        <TabsContent value="pending" className="space-y-3 mt-4">
          {pending.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-slate-400">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>{search ? 'Engar niðurstöður' : 'Engar umsóknir í bið'}</p>
            </CardContent></Card>
          ) : pending.map(app => (
            <Card key={app.id} className="border-l-4 border-amber-400 shadow-sm" data-testid={`app-card-${app.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-slate-900 text-lg">{app.company_name}</h3>
                        <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 text-xs">Í bið</Badge>
                      </div>
                      <p className="text-sm text-slate-500">Kennitala: {app.kennitala}</p>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 mt-3 text-sm">
                        <div className="flex items-center gap-1.5 text-slate-600"><User2 name={app.contact_name} /></div>
                        <div className="flex items-center gap-1.5 text-slate-600"><MailRow email={app.contact_email} /></div>
                        <div className="flex items-center gap-1.5 text-slate-600"><PhoneRow phone={app.contact_phone} /></div>
                        <div className="flex items-center gap-1.5 text-slate-600"><Car className="h-3.5 w-3.5 text-slate-400" />{app.estimated_fleet_size || '–'}</div>
                      </div>

                      {/* Expand */}
                      {(app.notes || app.company_address || app.vehicle_types) && (
                        <button className="mt-2 text-xs text-blue-600 flex items-center gap-1" onClick={() => toggleExpand(app.id)}>
                          {expanded[app.id] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {expanded[app.id] ? 'Fela' : 'Sýna meira'}
                        </button>
                      )}
                      {expanded[app.id] && (
                        <div className="mt-2 bg-slate-50 rounded p-3 text-sm text-slate-600 space-y-1">
                          {app.company_address && <p><strong>Heimilisfang:</strong> {app.company_address}</p>}
                          {app.vehicle_types && <p><strong>Tegund bíla:</strong> {app.vehicle_types}</p>}
                          {app.notes && <p><strong>Athugasemdir:</strong> {app.notes}</p>}
                        </div>
                      )}

                      <p className="text-xs text-slate-400 mt-2">Móttekin: {moment(app.created_at).format('DD.MM.YYYY HH:mm')}</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => openApprove(app)} data-testid={`approve-btn-${app.id}`}>
                      <Check className="h-3.5 w-3.5 mr-1" />Samþykkja
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => openReject(app)} data-testid={`reject-btn-${app.id}`}>
                      <X className="h-3.5 w-3.5 mr-1" />Hafna
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* APPROVED */}
        <TabsContent value="approved" className="space-y-3 mt-4">
          {approved.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-slate-400">
              <Check className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>{search ? 'Engar niðurstöður' : 'Engar samþykktar umsóknir'}</p>
            </CardContent></Card>
          ) : approved.map(app => (
            <Card key={app.id} className="border-l-4 border-green-400 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div>
                        <h3 className="font-bold text-slate-900 text-lg">{app.company_name}</h3>
                        <p className="text-sm text-slate-500">Kennitala: {app.kennitala}</p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" className="text-indigo-600 border-indigo-300 hover:bg-indigo-50 gap-1.5" onClick={() => openJobs(app)} data-testid={`jobs-btn-${app.id}`}>
                          <BarChart2 className="h-3.5 w-3.5" />Skoða verk
                        </Button>
                        <Button size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-50 gap-1.5" onClick={() => openSendLink(app)} data-testid={`link-btn-${app.id}`}>
                          <Send className="h-3.5 w-3.5" />Senda hlekk
                        </Button>
                        <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50 gap-1.5" onClick={() => openEdit(app)} data-testid={`edit-btn-${app.id}`}>
                          <Edit2 className="h-3.5 w-3.5" />Breyta stillingum
                        </Button>
                      </div>
                    </div>

                    {/* Payment settings chips */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="inline-flex items-center gap-1 bg-green-50 text-green-800 border border-green-200 rounded-full px-3 py-0.5 text-xs font-medium">
                        <Percent className="h-3 w-3" />{app.discount || 0}% afsláttur
                      </span>
                      <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-800 border border-blue-200 rounded-full px-3 py-0.5 text-xs font-medium">
                        <Clock className="h-3 w-3" />{app.payment_terms || 30} daga greiðslufrestur
                      </span>
                      <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-800 border border-purple-200 rounded-full px-3 py-0.5 text-xs font-medium">
                        <CreditCard className="h-3 w-3" />{PAYMENT_METHODS.find(m => m.value === app.payment_method)?.label || 'Reikningur'}
                      </span>
                      {app.credit_limit > 0 && (
                        <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-800 border border-orange-200 rounded-full px-3 py-0.5 text-xs font-medium">
                          Lánsmörk: {Number(app.credit_limit).toLocaleString('is-IS')} kr.
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 mt-3 text-sm">
                      <div className="flex items-center gap-1.5 text-slate-600"><User2 name={app.contact_name} /></div>
                      <div className="flex items-center gap-1.5 text-slate-600"><MailRow email={app.contact_email} /></div>
                      <div className="flex items-center gap-1.5 text-slate-600"><Car className="h-3.5 w-3.5 text-slate-400" />{app.estimated_fleet_size || '–'}</div>
                    </div>
                    {app.internal_notes && (
                      <p className="mt-2 text-xs text-slate-500 bg-slate-50 rounded px-2 py-1">{app.internal_notes}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-2">Samþykkt: {app.approved_at ? moment(app.approved_at).format('DD.MM.YYYY HH:mm') : '–'} {app.approved_by ? `· ${app.approved_by}` : ''}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* REJECTED */}
        <TabsContent value="rejected" className="space-y-3 mt-4">
          {rejected.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-slate-400">
              <X className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>{search ? 'Engar niðurstöður' : 'Engar hafnaðar umsóknir'}</p>
            </CardContent></Card>
          ) : rejected.map(app => (
            <Card key={app.id} className="border-l-4 border-red-300 opacity-80 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <X className="h-5 w-5 text-red-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-700">{app.company_name}</h3>
                    <p className="text-sm text-slate-500">{app.contact_name} · {app.contact_email}</p>
                    {app.rejection_reason && (
                      <div className="mt-2 flex items-start gap-1.5 text-sm text-red-700 bg-red-50 rounded px-2 py-1.5">
                        <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <span>{app.rejection_reason}</span>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-2">Hafnað: {moment(app.updated_at).format('DD.MM.YYYY HH:mm')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* APPROVE DIALOG */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Check className="h-5 w-5" />Samþykkja umsókn
            </DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-5">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <p className="font-semibold text-blue-900">{selectedApp.company_name}</p>
                <p className="text-sm text-blue-700">{selectedApp.contact_name} · {selectedApp.contact_email}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Afsláttur (%)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number" min="0" max="100"
                      value={approveForm.discount}
                      onChange={e => setApproveForm(f => ({ ...f, discount: parseInt(e.target.value) || 0 }))}
                      className="text-center font-bold text-lg"
                      data-testid="approve-discount"
                    />
                    <Percent className="h-5 w-5 text-blue-500 flex-shrink-0" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Greiðslufrestur</Label>
                  <Select value={String(approveForm.payment_terms)} onValueChange={v => setApproveForm(f => ({ ...f, payment_terms: parseInt(v) }))}>
                    <SelectTrigger className="mt-1" data-testid="approve-payment-terms">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map(d => <SelectItem key={d} value={String(d)}>{d} dagar</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Greiðslumáti</Label>
                  <Select value={approveForm.payment_method} onValueChange={v => setApproveForm(f => ({ ...f, payment_method: v }))}>
                    <SelectTrigger className="mt-1" data-testid="approve-payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Lánsmörk (kr.)</Label>
                  <Input
                    type="number" min="0"
                    placeholder="0 = engin mörk"
                    value={approveForm.credit_limit || ''}
                    onChange={e => setApproveForm(f => ({ ...f, credit_limit: parseInt(e.target.value) || 0 }))}
                    className="mt-1"
                    data-testid="approve-credit-limit"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Innri athugasemdir (sýnast ekki viðskiptavini)</Label>
                <Textarea
                  placeholder="Athugasemdir fyrir starfsfólk..."
                  value={approveForm.internal_notes}
                  onChange={e => setApproveForm(f => ({ ...f, internal_notes: e.target.value }))}
                  className="mt-1 resize-none"
                  rows={2}
                  data-testid="approve-notes"
                />
              </div>

              <div className="bg-green-50 rounded p-3 text-xs text-green-800 border border-green-200 space-y-1">
                <p className="font-medium">Þegar samþykkt:</p>
                <p>· Tölvupóstur sendur sjálfkrafa til {selectedApp.contact_email}</p>
                <p>· Fyrirtækið verður skráð sem viðskiptavinur með völdum stillingum</p>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setApproveDialogOpen(false)}>Hætta við</Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleApprove} disabled={approving} data-testid="confirm-approve-btn">
                  {approving ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />Samþykki...</> : <><Check className="h-4 w-4 mr-1.5" />Staðfesta samþykki</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* REJECT DIALOG */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <X className="h-5 w-5" />Hafna umsókn
            </DialogTitle>
          </DialogHeader>
          {rejectApp && (
            <div className="space-y-4">
              <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                <p className="font-semibold text-red-900">{rejectApp.company_name}</p>
                <p className="text-sm text-red-700">{rejectApp.contact_email}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">Ástæða höfnunar (valkvætt)</Label>
                <Textarea
                  placeholder="T.d. uppfyllir ekki skilyrði, vantar gögn..."
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  className="mt-1 resize-none"
                  rows={3}
                  data-testid="reject-reason"
                />
                <p className="text-xs text-slate-500 mt-1">Ástæðan verður send í tölvupósti til umsækjanda</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setRejectDialogOpen(false)}>Hætta við</Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleReject} disabled={rejecting} data-testid="confirm-reject-btn">
                  {rejecting ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />Hafnar...</> : <><X className="h-4 w-4 mr-1.5" />Staðfesta höfnun</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* SEND PORTAL LINK DIALOG */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Link2 className="h-5 w-5" />Senda aðgangshlekk
            </DialogTitle>
          </DialogHeader>
          {linkApp && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 border text-sm">
                <p className="font-semibold text-slate-900">{linkApp.company_name}</p>
                <p className="text-slate-500 text-xs mt-0.5">Kennitala: {linkApp.kennitala} · PIN: <strong>{linkApp.kennitala?.slice(-4) || '****'}</strong></p>
              </div>

              {/* Portal URL preview */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-600 font-medium mb-1">Hlekkur sem verður sendur:</p>
                <div className="flex items-center gap-2">
                  <code className="text-blue-900 font-mono text-sm flex-1">abon.is/portal</code>
                  <button onClick={() => copyPortalUrl('https://abon.is/portal')} className="text-blue-500 hover:text-blue-700 flex-shrink-0">
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Send via email */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="send_email" checked={linkForm.send_email} onChange={e => setLinkForm(f => ({...f, send_email: e.target.checked}))} className="rounded" />
                  <Label htmlFor="send_email" className="flex items-center gap-1.5 cursor-pointer"><Mail className="h-4 w-4 text-blue-500" />Senda tölvupóst</Label>
                </div>
                {linkForm.send_email && (
                  <Input placeholder="netfang@fyrirtaeki.is" type="email" value={linkForm.email} onChange={e => setLinkForm(f => ({...f, email: e.target.value}))} data-testid="link-email" />
                )}
              </div>

              {/* Send via SMS */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="send_sms" checked={linkForm.send_sms} onChange={e => setLinkForm(f => ({...f, send_sms: e.target.checked}))} className="rounded" />
                  <Label htmlFor="send_sms" className="flex items-center gap-1.5 cursor-pointer"><MessageSquare className="h-4 w-4 text-green-500" />Senda SMS</Label>
                </div>
                {linkForm.send_sms && (
                  <Input placeholder="5551234" value={linkForm.phone} onChange={e => setLinkForm(f => ({...f, phone: e.target.value}))} data-testid="link-phone" />
                )}
              </div>

              {linkPortalUrl && (
                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800 flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0" />Hlekkur sendur!
                </div>
              )}

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setLinkDialogOpen(false)}>Loka</Button>
                <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-1.5" onClick={handleSendPortalLink} disabled={linkSending} data-testid="send-link-btn">
                  {linkSending ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />Sendir...</> : <><Send className="h-4 w-4" />Senda</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* JOBS / HISTORY DIALOG */}
      <Dialog open={jobsDialogOpen} onOpenChange={setJobsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <BarChart2 className="h-5 w-5" />{jobsApp?.company_name} – Verkasaga
            </DialogTitle>
          </DialogHeader>

          {jobsLoading ? (
            <div className="py-16 text-center">
              <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Hleð sögu...</p>
            </div>
          ) : jobsData ? (
            <div className="space-y-5">
              {/* Stats row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Verk alls', value: jobsData.stats.total_jobs, color: 'slate' },
                  { label: 'Í gangi', value: jobsData.stats.active_jobs, color: 'amber' },
                  { label: 'Lokið', value: jobsData.stats.completed_jobs, color: 'green' },
                  { label: 'Heildarútgjöld', value: `${Number(jobsData.stats.total_spent || 0).toLocaleString('is-IS')} kr.`, color: 'blue' },
                ].map(s => (
                  <div key={s.label} className={`bg-${s.color}-50 border border-${s.color}-200 rounded-lg p-3 text-center`}>
                    <div className={`font-bold text-lg text-${s.color}-800`}>{s.value}</div>
                    <div className={`text-xs text-${s.color}-600`}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Plates */}
              {jobsData.plates.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-700 flex items-center gap-1.5 mb-2 text-sm">
                    <Car className="h-4 w-4 text-slate-500" />Bílar ({jobsData.plates.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {jobsData.plates.map(p => (
                      <div key={p.plate} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-sm">
                        <span className="font-bold text-slate-900 font-mono">{p.plate}</span>
                        <span className="text-slate-400 text-xs">{p.total_visits}x</span>
                        {p.last_visit && <span className="text-slate-400 text-xs">{moment(p.last_visit).format('DD.MM.YY')}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Jobs list */}
              <div>
                <h3 className="font-semibold text-slate-700 flex items-center gap-1.5 mb-2 text-sm">
                  <ListChecks className="h-4 w-4 text-slate-500" />Verk ({jobsData.jobs.length})
                </h3>
                {jobsData.jobs.length === 0 ? (
                  <div className="py-8 text-center text-slate-400 text-sm bg-slate-50 rounded-lg">Engin verk skráð</div>
                ) : (
                  <div className="space-y-1.5 max-h-80 overflow-y-auto">
                    {jobsData.jobs.map(job => (
                      <div key={job.id} className="flex items-center justify-between gap-3 bg-white border border-slate-100 rounded-lg px-3 py-2.5 hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono font-bold text-sm text-slate-900 flex-shrink-0">{job.plate}</span>
                          <span className="text-sm text-slate-600 truncate">{job.service_name || '–'}</span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className="text-xs text-slate-400">
                            {job.scheduled_date ? moment(job.scheduled_date).format('DD.MM.YY') : moment(job.created_at).format('DD.MM.YY')}
                          </span>
                          <StatusBadge status={job.status} />
                          {job.total > 0 && (
                            <span className="text-sm font-medium text-slate-700 w-24 text-right">{Number(job.total).toLocaleString('is-IS')} kr.</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* EDIT SETTINGS DIALOG */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Edit2 className="h-5 w-5" />Breyta stillingum
            </DialogTitle>
          </DialogHeader>
          {editApp && (
            <div className="space-y-5">
              <div className="bg-slate-50 rounded-lg p-3 border">
                <p className="font-semibold text-slate-900">{editApp.company_name}</p>
                <p className="text-sm text-slate-500">{editApp.contact_name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Afsláttur (%)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number" min="0" max="100"
                      value={editForm.discount}
                      onChange={e => setEditForm(f => ({ ...f, discount: parseInt(e.target.value) || 0 }))}
                      className="text-center font-bold"
                      data-testid="edit-discount"
                    />
                    <Percent className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Greiðslufrestur</Label>
                  <Select value={String(editForm.payment_terms)} onValueChange={v => setEditForm(f => ({ ...f, payment_terms: parseInt(v) }))}>
                    <SelectTrigger className="mt-1" data-testid="edit-payment-terms">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_TERMS.map(d => <SelectItem key={d} value={String(d)}>{d} dagar</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Greiðslumáti</Label>
                  <Select value={editForm.payment_method} onValueChange={v => setEditForm(f => ({ ...f, payment_method: v }))}>
                    <SelectTrigger className="mt-1" data-testid="edit-payment-method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">Lánsmörk (kr.)</Label>
                  <Input
                    type="number" min="0"
                    placeholder="0 = engin mörk"
                    value={editForm.credit_limit || ''}
                    onChange={e => setEditForm(f => ({ ...f, credit_limit: parseInt(e.target.value) || 0 }))}
                    className="mt-1"
                    data-testid="edit-credit-limit"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Innri athugasemdir</Label>
                <Textarea
                  value={editForm.internal_notes}
                  onChange={e => setEditForm(f => ({ ...f, internal_notes: e.target.value }))}
                  className="mt-1 resize-none"
                  rows={2}
                  data-testid="edit-notes"
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setEditDialogOpen(false)}>Hætta við</Button>
                <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn">
                  {saving ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />Vista...</> : 'Vista stillingar'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Mini helper components
function User2({ name }) {
  return <><Building2 className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" /><span className="truncate">{name || '–'}</span></>;
}
function MailRow({ email }) {
  return <><Mail className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" /><span className="truncate">{email || '–'}</span></>;
}
function PhoneRow({ phone }) {
  return <><Phone className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" /><span>{phone || '–'}</span></>;
}

const STATUS_CONFIG = {
  pending:    { label: 'Í bið',     cls: 'bg-amber-100 text-amber-800' },
  in_progress:{ label: 'Í gangi',   cls: 'bg-blue-100 text-blue-800' },
  paused:     { label: 'Í pásu',    cls: 'bg-orange-100 text-orange-800' },
  done:       { label: 'Lokið',     cls: 'bg-green-100 text-green-800' },
  paid:       { label: 'Greitt',    cls: 'bg-emerald-100 text-emerald-800' },
  cancelled:  { label: 'Afbókað',   cls: 'bg-red-100 text-red-800' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`inline-block text-xs font-medium rounded-full px-2 py-0.5 ${cfg.cls}`}>{cfg.label}</span>;
}

export default CorporateManagement;
